import { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { KanbanInstancia, InstanciaTarefa, tarefaTemTimer, calcularTempoRestante } from "./kanbanTypes";
import { KanbanModulo } from "../Configuracoes/TabKanbanModulos";

type Props = {
    open: boolean;
    onClose: () => void;
    instancias: KanbanInstancia[];
    modulos: KanbanModulo[];
};

export default function ModalAtrasadas({ open, onClose, instancias, modulos }: Props) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!open) return;
        setNow(Date.now());
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, [open]);

    const blocosAtrasados = useMemo(() => {
        const result: { instancia: KanbanInstancia; tarefasAtrasadas: InstanciaTarefa[] }[] = [];
        for (const inst of instancias) {
            const atrasadas: InstanciaTarefa[] = [];
            for (const t of inst.tarefas) {
                if (!tarefaTemTimer(t)) continue;
                const info = calcularTempoRestante(t, now);
                if (info && info.expirado) atrasadas.push(t);
            }
            if (atrasadas.length > 0) result.push({ instancia: inst, tarefasAtrasadas: atrasadas });
        }
        return result;
    }, [instancias, now]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <WarningAmberIcon sx={{ color: "#d32f2f" }} />
                <Typography variant="h6" fontWeight="bold">Tarefas Atrasadas</Typography>
            </DialogTitle>
            <DialogContent dividers>
                {blocosAtrasados.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={3}>
                        Nenhuma tarefa atrasada no momento.
                    </Typography>
                ) : (
                    blocosAtrasados.map(({ instancia, tarefasAtrasadas }, idx) => {
                        const moduloAtual = modulos.find(m => m.id_kanban_modulo === instancia.id_kanban_modulo);
                        return (
                            <Box key={instancia.id_kanban_instancia} sx={{ mb: idx < blocosAtrasados.length - 1 ? 2 : 0 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <ViewKanbanIcon sx={{ fontSize: 18, color: "#1565c0" }} />
                                    <Typography fontSize={14} fontWeight={700}>
                                        {instancia.nome_instancia}
                                    </Typography>
                                    {instancia.nome_grupo && (
                                        <Chip size="small" label={instancia.nome_grupo} variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                                    )}
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mb: 1, ml: 3.5 }}>
                                    <Typography fontSize={12} color="text.secondary">Módulo atual:</Typography>
                                    <Chip
                                        size="small"
                                        label={moduloAtual?.nome_modulo || "—"}
                                        sx={{ fontSize: 11, height: 20, bgcolor: "#e3f2fd", color: "#1565c0" }}
                                    />
                                    {instancia.nome_responsavel && (
                                        <>
                                            <Typography fontSize={12} color="text.secondary" sx={{ ml: 1 }}>
                                                Responsável:
                                            </Typography>
                                            <Chip
                                                size="small"
                                                icon={<PersonIcon sx={{ fontSize: 13 }} />}
                                                label={instancia.nome_responsavel}
                                                sx={{ fontSize: 11, height: 20, bgcolor: "#e8eaf6", color: "#283593" }}
                                            />
                                        </>
                                    )}
                                </Box>
                                {tarefasAtrasadas.map(tarefa => {
                                    const info = calcularTempoRestante(tarefa, now);
                                    return (
                                        <Box
                                            key={tarefa.id_kanban_instancia_tarefa}
                                            sx={{
                                                display: "flex", alignItems: "center", gap: 1,
                                                ml: 3.5, mb: 0.5, py: 0.6, px: 1.2,
                                                bgcolor: "#fff5f5", borderRadius: 1,
                                                border: "1px solid #ffcdd2",
                                            }}
                                        >
                                            <AccessTimeIcon sx={{ fontSize: 15, color: "#d32f2f" }} />
                                            <Typography fontSize={13} sx={{ flex: 1 }}>
                                                {tarefa.nome_tarefa}
                                            </Typography>
                                            {tarefa.nome_modulo && (
                                                <Chip
                                                    size="small"
                                                    label={tarefa.nome_modulo}
                                                    sx={{ fontSize: 10, height: 18, bgcolor: "#fce4ec", color: "#c62828" }}
                                                />
                                            )}
                                            {info && (
                                                <Typography fontSize={12} fontWeight={700} color="#d32f2f">
                                                    {info.texto}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })}
                                {idx < blocosAtrasados.length - 1 && <Divider sx={{ mt: 2 }} />}
                            </Box>
                        );
                    })
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Button onClick={onClose} variant="outlined" color="inherit">Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}
